/**
 * PromptEditor — one agent's system prompt: edit, dry-run, history. It
 * renders as one SettingsSection on /settings/prompts: the prompt's name,
 * live version and byline on the left, the tabs on the right.
 *
 * "Save" appends a revision and makes it live (history is append-only), so
 * "Reset to default" is itself a save whose body is the shipped default —
 * the live pointer always means something.
 */

import { useState } from 'react'
import { toast } from 'sonner'

import type { PromptTestResponse } from '@/api/generated/model'
import { PromptTestPanel } from './PromptTestPanel'
import { PromptVersionList } from './PromptVersionList'
import { SettingsSection } from '@/components/settings/SettingsSection'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import {
  useActivatePromptVersion,
  usePromptVersions,
  useSavePromptVersion,
  useTestPrompt,
  type AgentPrompt,
} from '@/hooks/useAgentPrompts'
import { formatRelativeTime } from '@/lib/utils'

/** Backend `detail`, falling back to a generic message. */
function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

interface PromptEditorProps {
  prompt: AgentPrompt
  readOnly: boolean
  /** Test anchor handed over by another page; opens on the Test tab. */
  initialAnchor?: string
}

export function PromptEditor({
  prompt,
  readOnly,
  initialAnchor,
}: PromptEditorProps): React.JSX.Element {
  const [body, setBody] = useState(prompt.body)
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [testResult, setTestResult] = useState<PromptTestResponse | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  const versionsQuery = usePromptVersions(prompt.kind)
  const versions = versionsQuery.data ?? []

  const saveMutation = useSavePromptVersion()
  const activateMutation = useActivatePromptVersion()
  const testMutation = useTestPrompt()

  const handleSave = (): void => {
    saveMutation.mutate(
      { kind: prompt.kind, data: { body } },
      {
        onSuccess: () => {
          setSelectedVersion(null)
          toast.success('Saved. This revision is now live')
        },
        onError: (error) => {
          toast.error(errorMessage(error, 'Could not save the prompt'))
        },
      }
    )
  }

  const handleActivate = (version: number): void => {
    activateMutation.mutate(
      { kind: prompt.kind, version },
      {
        onSuccess: (response) => {
          if (response.status !== 200) {
            toast.error('Could not activate that revision')
            return
          }
          // Re-seed the box from what just went live. `body` is local state
          // seeded once, so a rollback would otherwise leave the previous
          // revision's text on screen, reading as unsaved changes — and a
          // Save would append that stale text over the revision restored.
          setBody(response.data.body)
          setSelectedVersion(null)
          toast.success(`v${String(version)} is now live`)
        },
        onError: (error) => {
          toast.error(errorMessage(error, 'Could not activate that revision'))
        },
      }
    )
  }

  const handleTest = (traceId: string): void => {
    testMutation.mutate(
      { kind: prompt.kind, data: { body, trace_id: traceId } },
      {
        onSuccess: (response) => {
          if (response.status === 200) {
            setTestResult(response.data)
            setTestError(null)
          }
        },
        onError: (error) => {
          setTestResult(null)
          setTestError(errorMessage(error, 'The test run failed'))
        },
      }
    )
  }

  const isBusy = saveMutation.isPending || activateMutation.isPending

  return (
    <SettingsSection
      title={prompt.display_name}
      badge={
        prompt.is_default ? (
          <Badge variant="outline">Default</Badge>
        ) : (
          <Badge variant="secondary">v{String(prompt.active_version ?? 1)}</Badge>
        )
      }
      description={prompt.description}
      aside={<PromptByline prompt={prompt} />}
    >
      <Tabs defaultValue={initialAnchor ? 'test' : 'edit'}>
        <TabsList>
          <TabsTrigger value="edit">Edit</TabsTrigger>
          {/* Only the dream judge has a dry run; the review chat is tried
              by asking a question on the review page (TRA-1082). */}
          {prompt.supports_test && <TabsTrigger value="test">Test</TabsTrigger>}
          <TabsTrigger value="history">
            History{versions.length > 0 ? ` · ${String(versions.length)}` : ''}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit">
          <PromptEditTab
            prompt={prompt}
            body={body}
            readOnly={readOnly}
            isBusy={isBusy}
            isSaving={saveMutation.isPending}
            onChange={setBody}
            onReset={() => {
              setBody(prompt.default_body)
            }}
            onSave={handleSave}
          />
        </TabsContent>

        {prompt.supports_test && (
          <TabsContent value="test">
            <PromptTestPanel
              kind={prompt.kind}
              isRunning={testMutation.isPending}
              result={testResult}
              error={testError}
              initialAnchor={initialAnchor}
              readOnly={readOnly}
              onRun={({ traceId }) => {
                handleTest(traceId)
              }}
            />
          </TabsContent>
        )}

        <TabsContent value="history">
          <PromptVersionList
            versions={versions}
            defaultBody={prompt.default_body}
            defaultIsLive={prompt.is_default}
            selectedVersion={selectedVersion}
            onSelect={setSelectedVersion}
            onActivate={handleActivate}
            isActivating={activateMutation.isPending}
            disabled={readOnly}
          />
        </TabsContent>
      </Tabs>
    </SettingsSection>
  )
}

/** Who last edited a team override and when; the shipped default has no byline. */
function PromptByline({ prompt }: { prompt: AgentPrompt }): React.JSX.Element | null {
  if (prompt.is_default || !prompt.updated_by) return null
  const when = prompt.updated_at ? ` · ${formatRelativeTime(prompt.updated_at)}` : ''
  return (
    <p className="pt-2 font-mono text-[11px] text-muted-foreground">
      Edited by {prompt.updated_by}
      {when}
    </p>
  )
}

function PromptEditTab({
  prompt,
  body,
  readOnly,
  isBusy,
  isSaving,
  onChange,
  onReset,
  onSave,
}: {
  prompt: AgentPrompt
  body: string
  readOnly: boolean
  isBusy: boolean
  isSaving: boolean
  onChange: (body: string) => void
  onReset: () => void
  onSave: () => void
}): React.JSX.Element {
  const isDirty = body !== prompt.body
  const isDefault = body === prompt.default_body
  return (
    <div className="space-y-3">
      <Textarea
        value={body}
        readOnly={readOnly}
        rows={14}
        spellCheck={false}
        aria-label={`${prompt.display_name} prompt`}
        onChange={(e) => {
          onChange(e.target.value)
        }}
        className="font-mono text-xs leading-relaxed"
      />
      <div className="flex flex-wrap items-center justify-end gap-3">
        <PlaceholderHint placeholders={prompt.placeholders} />
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" disabled={readOnly || isBusy || isDefault} onClick={onReset}>
            Reset to default
          </Button>
          <Button size="sm" disabled={readOnly || isBusy || !isDirty} onClick={onSave}>
            {isSaving ? 'Saving...' : 'Save & activate'}
          </Button>
        </div>
      </div>
      {isDirty && (
        <p className="text-xs text-warning">
          {prompt.supports_test
            ? 'Unsaved changes. Testing uses this draft; live runs use the saved revision.'
            : 'Unsaved changes. The chat uses the saved revision.'}
        </p>
      )}
    </div>
  )
}

function PlaceholderHint({ placeholders }: { placeholders: string[] }): React.JSX.Element {
  if (placeholders.length === 0) {
    return (
      <p className="mr-auto text-xs text-muted-foreground">
        Plain text. Edits apply to the next question asked.
      </p>
    )
  }
  return (
    <p className="mr-auto text-xs text-muted-foreground">
      Placeholders:{' '}
      {placeholders.map((name) => (
        <code key={name} className="mr-1 bg-muted px-1">
          ${name}
        </code>
      ))}
      (write a literal dollar sign as <code>$$</code>).
    </p>
  )
}
